import { AxiosError } from 'axios';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { SearchForm } from './SearchForm';
import { SearchPlaceholder } from './SearchPlaceholder';
import { SearchProductCard } from './SearchProductCard';
import { SearchSkeleton } from './SearchSkeleton';

import { NoContentFallback } from '@/components/NoContentFallBack';
import { PaginationV2 } from '@/components/PaginationV2';
import { ROUTE_PATHS } from '@/config/constants';
import { useSearchProducts } from '@/hooks/useSearchProducts';
import { SearchDto, SearchProduct } from '@/services/ProductsService';

export const Search = () => {
  const [sessionId] = useState(() => String(Number(new Date())));
  const [products, setProducts] = useState<SearchProduct[]>([]);
  const [totalPages, setTotalPages] = useState(0);

  // Estado interno em vez de URL
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Personalization.components.Search',
  });

  const searchDto: SearchDto = useMemo(
    () => ({
      userId: null,
      sessionId,
    }),
    [sessionId],
  );

  const { searchProducts, isLoading } = useSearchProducts();

  const performSearch = useCallback(async () => {
    const query = searchTerm.trim();
    if (!query) {
      setProducts([]);
      setTotalPages(0);
      return;
    }

    try {
      const response = await searchProducts({
        searchDto,
        searchParams: {
          query,
          page: currentPage,
          limit: 8,
        },
      });

      setProducts(response.items);
      setTotalPages(response.totalPages);
    } catch (error) {
      setProducts([]);
      setTotalPages(0);
      const errorMessage =
        error instanceof AxiosError && error.response?.data?.message
          ? error.response.data.message
          : t('searchError');
      toast.error(errorMessage);
    }
  }, [searchProducts, searchDto, searchTerm, currentPage, t]);

  useEffect(() => {
    performSearch();
  }, [performSearch]);

  function handleSearchSubmit(term: string) {
    const next = term.trim();
    if (next) {
      // reset de página ao submeter nova busca
      setSearchTerm(next);
      setCurrentPage(1);
    } else {
      handleClear();
    }
  }

  function handleClear() {
    setSearchTerm('');
    setCurrentPage(1);
    setProducts([]);
    setTotalPages(0);
  }

  function nextPage() {
    setCurrentPage((p) => Math.min(p + 1, Math.max(1, totalPages)));
  }

  function previousPage() {
    setCurrentPage((p) => Math.max(1, p - 1));
  }

  const hasSearch = searchTerm.trim().length > 0;

  const showPlaceholder = !hasSearch && !isLoading;
  const showSkeleton = hasSearch && isLoading;
  const showProducts = hasSearch && !isLoading && products.length > 0;
  const showNoContent = hasSearch && !isLoading && products.length === 0;
  const showPagination =
    hasSearch && !isLoading && totalPages > 1 && products.length >= 0;

  return (
    <div className="border-t border-gray-200 bg-gray-50 p-6">
      <div className="mt-4 space-y-2">
        <SearchForm
          searchQuery={searchTerm}
          isLoading={isLoading}
          onSearchSubmit={handleSearchSubmit}
          onClear={handleClear}
        />

        <div className="min-h-[400px] space-y-2">
          {showPlaceholder && <SearchPlaceholder />}

          {showSkeleton && <SearchSkeleton />}

          {showProducts && (
            <div className="grid grid-cols-4 gap-4">
              {products.map((product) => (
                <Link
                  to={`${ROUTE_PATHS.VISION_ENRICHMENT}/${product.id}`}
                  key={product.id}
                  target="_blank"
                >
                  <SearchProductCard product={product} />
                </Link>
              ))}
            </div>
          )}

          {showNoContent && (
            <NoContentFallback
              title={t('noContent.title')}
              description={t('noContent.description', {
                searchTerm,
              })}
            />
          )}

          {showPagination && (
            <PaginationV2
              className="mt-6"
              page={currentPage}
              totalPages={totalPages}
              hasNextPage={currentPage < totalPages}
              hasPreviousPage={currentPage > 1}
              nextPage={nextPage}
              previousPage={previousPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};
